import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Breadcrumbs,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Divider,
  Autocomplete,
  Alert,
  Tooltip,
} from "@mui/material";
import { FormControlLabel, Checkbox } from "@mui/material";
import { X } from "lucide-react";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import axiosInstance from "../../axios/axiosInstance";
import { workSetupOptions } from "../career_journey/utils/utils";
import CareerJourneyPage from "../career_journey/CareerJourneyPage";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import TableViewIcon from "@mui/icons-material/TableView";
import GridViewIcon from "@mui/icons-material/GridView";
import VerifiedIcon from "@mui/icons-material/Verified";
import { degreeOptions, certificationOptions } from "./Constants";
import {
  formatCertDate,
  resolveCertificationNoExpiry,
} from "../../utils/profileFieldNormalization";
import { toast } from "react-toastify";
import SkillMatchingDialog from "./SkillMatchingDialog";

const levels = ["Beginner", "Intermediate", "Advanced", "Expert"];

const RoleCandidates = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = Cookies.get("token");

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const functionId = params.functionId ?? searchParams.get("functionId");
  const roleIds = params.roleIds ?? searchParams.get("roleIds") ?? "";
  const jobId = params.jobId;

  const [candidates, setCandidates] = useState([]);
  const [rolesMap, setRolesMap] = useState({});
  const [functionName, setFunctionName] = useState("");
  const [roleNames, setRoleNames] = useState([]);
  const [candidateDetails, setCandidateDetails] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillFilters, setSkillFilters] = useState({
    must: {},
    should: {},
    could: {},
  });

  const cap = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  const formatSkillQuery = (skillsObj) =>
    JSON.stringify(
      Object.entries(skillsObj).map(([skill, level]) => ({
        Category: skill,
        level: level,
      })),
    );

  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  // Skill matching score state
  const [skillMatchLoadingId, setSkillMatchLoadingId] = useState(null);
  const [skillMatchDialogOpen, setSkillMatchDialogOpen] = useState(false);
  const [selectedSkillMatchData, setSelectedSkillMatchData] = useState(null);
  const [comment, setComment] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserCareerJourneyId, setSelectedUserCareerJourneyId] =
    useState("");
  const [selectedUserJobRoleId, setSelectedUserJobRoleId] = useState("");
  const [modelOpen, setModelOpen] = useState(false);
  const [selectedJobPosting, setSelectedJobPosting] = useState("");
  const [selectedUserPeerReview, setSelectedUserPeerReview] = useState(false);

  const [peerReviewFilter, setPeerReviewFilter] = useState("");
  const [certStatusFilter, setCertStatusFilter] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [degreeFilter, setDegreeFilter] = useState("");
  const [institutionFilter, setInstitutionFilter] = useState("");
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [institutionQuery, setInstitutionQuery] = useState("");
  const [degreeQuery, setDegreeQuery] = useState("");
  const [certNameFilter, setCertNameFilter] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");

  const [expandedRows, setExpandedRows] = useState({});
  const [filterKey, setFilterKey] = useState(0);

  const selectedRoleIds = useMemo(
    () =>
      roleIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => parseInt(id, 10)),
    [roleIds],
  );

  // Debounce field filter
  useEffect(() => {
    const handler = setTimeout(() => setFieldFilter(fieldValue), 500);
    return () => clearTimeout(handler);
  }, [fieldValue]);

  const handleCareerJourneyModelOpen = (user) => {
    const details =
      candidateDetails.find((d) => d.user_id === user.user_id) || {};
    const hasPeerReview = [
      user?.peerReview,
      user?.peer_review,
      details?.peerReview,
      details?.peer_review,
    ].some((val) => val === 1 || val === true || val === "1");

    setModelOpen(true);
    setSelectedUserJobRoleId(user.id);
    setSelectedUserCareerJourneyId(user.user_id);
    setSelectedUserPeerReview(hasPeerReview);
  };

  const handleCareerJourneyClose = () => {
    setModelOpen(false);
    setSelectedUserJobRoleId("");
    setSelectedUserCareerJourneyId("");
    setSelectedUserPeerReview(false);
  };

  const handleOpen = (user) => {
    setSelectedUser(user);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedUser(null);
    setOpen(false);
    setComment("");
    setError("");
    setSelectedJobPosting("");
  };

  // Fetch Skills
  const fetchSkills = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(
        `api/profile/job-role-skills/${functionId}/${roleIds}`,
      );
      if (!res.data) throw new Error("Failed to fetch skills");

      const uniqueSkills = Array.from(
        new Set([
          ...(res.data.must_have_skills || []),
          ...(res.data.should_have_skills || []),
          ...(res.data.could_have_skills || []),
        ]),
      );
      setSkills(uniqueSkills);
    } catch (err) {
      setError("Could not load skills.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!functionId || !roleIds) return;
    fetchSkills();
  }, [functionId, roleIds]);

  // Fetch function name
  useEffect(() => {
    if (!functionId) return;
    const fetchFunctionName = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/job-functions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.ok) {
        const data = await res.json();
        const func = data.find((f) => String(f.id) === String(functionId));
        if (func) setFunctionName(func.name);
      }
    };
    fetchFunctionName();
  }, [functionId, token]);

  // Fetch roles
  useEffect(() => {
    if (!functionId) return;
    const fetchRoles = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/job-roles/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach((r) => {
          map[r.id] = r.name;
        });
        setRolesMap(map);
        setRoleNames(
          data.filter((r) => selectedRoleIds.includes(r.id)).map((r) => r.name),
        );
      }
    };
    fetchRoles();
  }, [functionId, roleIds, token]);

  // Fetch institutions, degrees, certifications (debounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (institutionQuery.length > 1) {
        try {
          const res = await axiosInstance.get(
            `/api/profile/institutes?name=${encodeURIComponent(
              institutionQuery,
            )}`,
          );
          setInstitutions(res.data);
        } catch (err) {
          console.error(err);
        }
      } else setInstitutions([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [institutionQuery]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (degreeQuery.length > 1) {
        try {
          const res = await axiosInstance.get(
            `/api/profile/programs?name=${encodeURIComponent(degreeQuery)}`,
          );
          setDegrees(res.data);
        } catch (err) {
          console.error(err);
        }
      } else setDegrees([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [degreeQuery]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (certNameFilter.length > 1) {
        try {
          const res = await axiosInstance.get(
            `/api/profile/certifications?name=${encodeURIComponent(
              certNameFilter,
            )}`,
          );
          setCertifications(res.data);
        } catch (err) {
          console.error(err);
        }
      } else setCertifications([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [certNameFilter]);

  // Fetch candidates
  const fetchCandidates = async (overrides = {}) => {
    if (!functionId || selectedRoleIds.length === 0) return;
    try {
      const params = new URLSearchParams();
      params.append("jobFunctionId", functionId);
      selectedRoleIds.forEach((id) => params.append("jobRoleId", id));
      if (jobId) {
        params.append("jobId", jobId);
      }

      // Use overrides if provided, otherwise use state
      const currentSkillFilters =
        overrides.skillFilters !== undefined
          ? overrides.skillFilters
          : skillFilters;
      const currentPeerReviewFilter =
        overrides.peerReviewFilter !== undefined
          ? overrides.peerReviewFilter
          : peerReviewFilter;
      const currentSelectedInstitution =
        overrides.selectedInstitution !== undefined
          ? overrides.selectedInstitution
          : selectedInstitution;
      const currentSelectedDegree =
        overrides.selectedDegree !== undefined
          ? overrides.selectedDegree
          : selectedDegree;
      const currentFieldFilter =
        overrides.fieldFilter !== undefined
          ? overrides.fieldFilter
          : fieldFilter;
      const currentSelectedCertificate =
        overrides.selectedCertificate !== undefined
          ? overrides.selectedCertificate
          : selectedCertificate;
      const currentCertStatusFilter =
        overrides.certStatusFilter !== undefined
          ? overrides.certStatusFilter
          : certStatusFilter;

      // Skill filters with levels
      Object.entries(currentSkillFilters).forEach(([category, skillsObj]) => {
        const formatted = formatSkillQuery(skillsObj);
        if (formatted) params.append(`${category}_skills`, formatted);
      });

      if (currentPeerReviewFilter)
        params.append("peerReview", currentPeerReviewFilter);
      if (currentSelectedInstitution)
        params.append("institution", currentSelectedInstitution?.value);

      if (currentSelectedDegree)
        params.append("degree", currentSelectedDegree?.value);
      if (currentFieldFilter) params.append("fieldOfStudy", currentFieldFilter);
      if (currentSelectedCertificate)
        params.append("certName", currentSelectedCertificate?.value);
      if (currentCertStatusFilter)
        params.append("certValidity", currentCertStatusFilter);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL
        }/api/profile/user-job-roles?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.ok) {
        const data = await res.json();
        setCandidates(data);
        return data;
      } else {
        console.error("Failed to fetch candidates");
        return null;
      }
    } catch (err) {
      console.error("Error fetching candidates:", err);
      return null;
    }
  };
  const handleToggle = (userId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId, selectedRoleIds]);

  // Fetch skill score on-demand when a user clicks the skill match chip
  const handleSkillMatchClick = async (userId) => {
    if (!jobId) return;
    setSkillMatchLoadingId(userId);
    try {
      const res = await axiosInstance.get(
        `api/profile/candidate/skill-score/${userId}/${jobId}`,
      );
      if (res.data) {
        setSelectedSkillMatchData(res.data);
        setSkillMatchDialogOpen(true);
      }
    } catch (err) {
      console.error("Error fetching skill score:", err);
    } finally {
      setSkillMatchLoadingId(null);
    }
  };

  // Fetch candidate details
  useEffect(() => {
    const userIds = [...new Set(candidates.map((c) => c.user_id))];
    if (userIds.length === 0) return;
    const fetchDetails = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL
        }/api/profile/candidates?ids=${userIds.join(",")}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        setCandidateDetails(data);
      }
    };
    fetchDetails();
  }, [candidates, token]);

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (e.target.value.trim() !== "") {
      setError("");
    }
  };

  // Handle comment submit
  const handleSubmit = async () => {
    if (!selectedUser) return;
    // if (!comment.trim()) {
    //   setError("Please enter a comment.");
    //   return;
    // }
    try {
      const response = await axiosInstance.post(
        `/api/profile/candidate/interest`,
        {
          candidate_user_id: selectedUser.user_id,
          corporate_comment: comment,
          user_job_role_id: selectedUser.id,
          ...(selectedJobPosting && { job_id: selectedJobPosting }),
          ...(!selectedJobPosting && jobId && { job_id: jobId }),
        },
      );

      if (response.status === 200) {
        setComment("");
        handleClose();
        // Refetch candidates data from backend
        const updatedCandidates = await fetchCandidates();
        // Refetch candidate details with updated candidates
        if (updatedCandidates && updatedCandidates.length > 0) {
          const userIds = [...new Set(updatedCandidates.map((c) => c.user_id))];
          if (userIds.length > 0) {
            const res = await fetch(
              `${process.env.REACT_APP_API_URL
              }/api/profile/candidates?ids=${userIds.join(",")}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (res.ok) {
              const data = await res.json();
              setCandidateDetails(data);
            }
          }
        }
      }
    } catch (error) {
      if (!jobId && !selectedJobPosting) {
        toast.error(
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
        );
      }
      console.error("API error:", error);
    }
  };

  const clearAllFilters = () => {
    // Fetch with empty filters first
    fetchCandidates({
      skillFilters: { must: {}, should: {}, could: {} },
      peerReviewFilter: false,
      selectedInstitution: null,
      selectedDegree: null,
      fieldFilter: "",
      selectedCertificate: null,
      certStatusFilter: "",
    });

    // Then reset state
    setSkillFilters({ must: {}, should: {}, could: {} });
    setDegreeQuery("");
    setFieldValue("");
    setCertNameFilter("");
    setPeerReviewFilter(false);
    setSelectedCertificate(null);
    setSelectedDegree(null);
    setFieldFilter("");
    setInstitutionQuery("");
    setSelectedInstitution(null);
    setCertStatusFilter("");
    setFilterKey((prev) => prev + 1);
  };

  return (
    <>
      <Box sx={{ p: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Typography
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            color="inherit"
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Back
          </Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ mb: 1 }}>
          Candidates for {functionName}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Showing candidates for role: {roleNames.join(", ")}
        </Typography>

        <Box sx={{ width: "100%", p: 0, mb: 2 }}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 3,
              width: "100%",
              mx: 0,
              px: 2,
            }}
          >
            <CardContent>
              {/* Header */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <FilterListIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Filter Candidates
                </Typography>
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      key={`degree-${filterKey}`}
                      size="small"
                      options={degreeOptions}
                      value={selectedDegree}
                      getOptionLabel={(option) => option.label || ""}
                      onChange={(e, value) => setSelectedDegree(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Degree"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Field of Study"
                      value={fieldValue}
                      onChange={(e) => setFieldValue(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      key={`cert-${filterKey}`}
                      freeSolo
                      size="small"
                      options={certificationOptions}
                      value={selectedCertificate}
                      getOptionLabel={(option) => option.label || ""}
                      // onInputChange={(e, value) => setCertNameFilter(value)}
                      onChange={(e, value) => setSelectedCertificate(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Certification Name"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: peerReviewFilter ? "#f3e5f5" : "white",
                        border: peerReviewFilter
                          ? "2px solid #7b1fa2"
                          : "1px solid #e0e0e0",
                        borderRadius: 2,
                        transition: "all 0.2s",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "#7b1fa2",
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                      onClick={() => setPeerReviewFilter(!peerReviewFilter)}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ px: 1 }}
                      >
                        <VerifiedIcon
                          sx={{
                            fontSize: 20,
                            color: peerReviewFilter
                              ? "#7b1fa2"
                              : "text.secondary",
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={peerReviewFilter}
                              onChange={(e) =>
                                setPeerReviewFilter(
                                  e.target.checked ? "1" : null,
                                )
                              }
                              size="small"
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#7b1fa2 !important",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#7b1fa2 !important",
                                  opacity: "0.5 !important",
                                },
                              }}
                            />
                          }
                          label={
                            <Tooltip title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility.">
                              <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
                                Hiring Manager Evaluated
                              </Typography>
                            </Tooltip>
                          }
                          sx={{ m: 0 }}
                        />
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>

              {/* Skills Filters */}
              {/* <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                }}
              > */}
              <Grid container spacing={2}>
                {[
                  {
                    type: "must",
                    label: "Must-have Skills",
                    color: "#d32f2f",
                  },
                  {
                    type: "should",
                    label: "Should-have Skills",
                    color: "#ed6c02",
                  },
                  {
                    type: "could",
                    label: "Could-have Skills",
                    color: "#0288d1",
                  },
                ].map(({ type, label, color }) => {
                  const selectedSkills = Object.entries(skillFilters[type]);
                  const remainingSkills = skills.filter(
                    (s) =>
                      !Object.keys(skillFilters.must).includes(s) &&
                      !Object.keys(skillFilters.should).includes(s) &&
                      !Object.keys(skillFilters.could).includes(s),
                  );

                  return (
                    <Grid item xs={12} md={4} key={type}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          borderRadius: 2,
                          backgroundColor: "white",
                          // borderTop: `3px solid ${color}`,
                        }}
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {label}
                            </Typography>
                            <Chip
                              label={`${selectedSkills.length}/3`}
                              size="small"
                              sx={{
                                backgroundColor:
                                  selectedSkills.length >= 3
                                    ? color
                                    : "#e0e0e0",
                                color:
                                  selectedSkills.length >= 3
                                    ? "white"
                                    : "text.secondary",
                                fontWeight: 600,
                              }}
                            />
                          </Stack>

                          <Autocomplete
                            key={`skill-${type}-${filterKey}`}
                            disabled={selectedSkills.length >= 3}
                            options={remainingSkills}
                            size="small"
                            value={null}
                            onChange={(e, value) => {
                              if (!value) return;
                              setSkillFilters((prev) => ({
                                ...prev,
                                [type]: {
                                  ...prev[type],
                                  [value]: "Beginner",
                                },
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={
                                  selectedSkills.length >= 3
                                    ? "Maximum reached"
                                    : "Add skill..."
                                }
                                variant="outlined"
                                fullWidth
                              />
                            )}
                          />

                          {selectedSkills.length > 0 && (
                            <Stack spacing={1} sx={{ mt: 2 }}>
                              {selectedSkills.map(([skill, level]) => (
                                <Paper
                                  key={skill}
                                  variant="outlined"
                                  sx={{
                                    p: 0.8,
                                    borderRadius: 1.5,
                                    backgroundColor: "#fafafa",
                                    // borderLeft: `3px solid ${color}`,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                      sx={{ flexGrow: 1 }}
                                    >
                                      {skill}
                                    </Typography>

                                    <TextField
                                      select
                                      value={level}
                                      size="small"
                                      sx={{
                                        width: 120,
                                        "& .MuiOutlinedInput-root": {
                                          backgroundColor: "white",
                                        },
                                      }}
                                      onChange={(e) =>
                                        setSkillFilters((prev) => ({
                                          ...prev,
                                          [type]: {
                                            ...prev[type],
                                            [skill]: e.target.value,
                                          },
                                        }))
                                      }
                                    >
                                      {levels.map((lvl) => (
                                        <MenuItem key={lvl} value={lvl}>
                                          {lvl}
                                        </MenuItem>
                                      ))}
                                    </TextField>

                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        setSkillFilters((prev) => {
                                          const updated = { ...prev[type] };
                                          delete updated[skill];
                                          return { ...prev, [type]: updated };
                                        })
                                      }
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {/* </Paper> */}

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" spacing={1}>
                  <Button
                    variant={viewMode === "table" ? "contained" : "outlined"}
                    // startIcon={<TableViewIcon />}
                    onClick={() => setViewMode("table")}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: 120,
                    }}
                  >
                    Table
                  </Button>
                  <Button
                    variant={viewMode === "grid" ? "contained" : "outlined"}
                    // startIcon={<GridViewIcon />}
                    onClick={() => setViewMode("grid")}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: 120,
                    }}
                  >
                    Grid
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<FilterAltOffIcon />}
                    onClick={clearAllFilters}
                    sx={{
                      minWidth: 180,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FilterAltIcon />}
                    onClick={fetchCandidates}
                    sx={{
                      minWidth: 150,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Apply Filter
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {loading ? (
          <Box
            sx={{
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 5,
            }}
          >
            <CircularProgress />
          </Box>
        ) : viewMode === "table" ? (
          <TableContainer
            component={Paper}
            sx={{ mt: 2, borderRadius: 3, boxShadow: 0 }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell
                    sx={{
                      width: "12%",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Candidate
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "18%",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Evaluation Status
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "12%",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Job Search Status
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "14%",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "14%",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Skills
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "12%",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Skill Matching
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "31%",
                      textAlign: "center",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography>No candidates found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  candidates.map((user) => {
                    const details =
                      candidateDetails.find(
                        (d) => d.user_id === user.user_id,
                      ) || {};
                    const hasPeerReview = [
                      user?.peerReview,
                      user?.peer_review,
                      details?.peerReview,
                      details?.peer_review,
                    ].some((val) => val === 1 || val === true || val === "1");
                    const showCareerJourney = user.has_career_journey !== 0;
                    const skills = user.skills || [];
                    const showAll = expandedRows[user.user_id] || false;
                    const visibleSkills = showAll ? skills : skills.slice(0, 3);
                    return (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                position: "relative",
                                display: "inline-flex",
                              }}
                            >
                              <Avatar>
                                {details.first_name
                                  ? details.first_name[0]
                                  : "?"}
                              </Avatar>
                              {hasPeerReview && (
                                <Tooltip title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility.">
                                  <VerifiedIcon
                                    sx={{
                                      position: "absolute",
                                      bottom: -4,
                                      right: -4,
                                      fontSize: 18,
                                      color: "#7b1fa2",
                                      bgcolor: "background.paper",
                                      borderRadius: "50%",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            <Box>
                              <Typography fontWeight={600}>
                                {details.masked_name || "Candidate"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        {/* <TableCell>
                          <Tooltip title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility.">
                            <Chip
                              label={
                                user.peer_review
                                  ? "Hiring Manager Evaluated"
                                  : "Not Hiring Manager Evaluated"
                              }
                              size="small"
                              sx={{
                                bgcolor: user.peer_review ? "#f3e5f5" : "#fdf7ffff",
                                color: user.peer_review ? "#7b1fa2" : "#ba68c8",
                                border: `1px solid ${user.peer_review ? "#7b1fa2" : "#ba68c8"
                                  }`,
                                fontWeight: 500,
                              }}
                            />
                          </Tooltip>
                        </TableCell> */}
                        <TableCell>
                          <Tooltip title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility.">
                            <Chip
                              label={
                                user.peer_review
                                  ? "Hiring Manager Evaluated"
                                  : "Not Hiring Manager Evaluated"
                              }
                              size="small"
                              color={user.peer_review ? "primary" : "default"}
                              variant={user.peer_review ? "filled" : "outlined"}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={details.job_search_status || "Not provided"}
                            size="small"
                            color={
                              details.job_search_status ? "primary" : "default"
                            }
                            variant={
                              details.job_search_status ? "filled" : "outlined"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.job_role_name || user.job_role_id}
                            size="small"
                            color="primary"
                          />
                        </TableCell>

                        <TableCell>
                          {skills.length > 0 ? (
                            <>
                              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                                {visibleSkills.map((skillObj, index) => {
                                  const skillName = Object.keys(skillObj)[0];
                                  const skillData = skillObj[skillName];

                                  return (
                                    <li key={index}>
                                      <Typography variant="body2">
                                        {`${skillName}`}
                                      </Typography>
                                    </li>
                                  );
                                })}
                              </ul>

                              {skills.length > 3 && (
                                <Button
                                  onClick={() => handleToggle(user.user_id)}
                                  size="small"
                                  sx={{ mt: 0.5, textTransform: "none" }}
                                >
                                  {showAll ? "Show Less" : "Show More"}
                                </Button>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No skills found
                            </Typography>
                          )}
                        </TableCell>

                        {/* Skill Match Score */}
                        <TableCell>
                          {jobId ? (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={skillMatchLoadingId === user.user_id}
                              onClick={() =>
                                handleSkillMatchClick(user.user_id)
                              }
                              sx={{
                                mt: 0.5,
                                textTransform: "none",
                              }}
                            >
                              {skillMatchLoadingId === user.user_id
                                ? "Loading..."
                                : "Skill Match Score"}
                            </Button>
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell sx={{ textAlign: "right" }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            {showCareerJourney && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                  handleCareerJourneyModelOpen(user)
                                }
                                sx={{
                                  fontSize: "0.8125rem",
                                }}
                              >
                                Career Journey
                              </Button>
                            )}

                            {!user.interest_status &&
                              !user.latest_general_interest && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<PersonAddIcon fontSize="small" />}
                                  onClick={() =>
                                    handleOpen({ ...user, ...details })
                                  }
                                  sx={{
                                    fontSize: "0.8125rem",
                                    textTransform: "none",
                                    fontWeight: 500,
                                  }}
                                >
                                  Interest
                                </Button>
                              )}

                            {(user.interest_status ||
                              user.latest_general_interest) && (
                                <Chip
                                  icon={
                                    <CheckRoundedIcon
                                      fontSize="small"
                                      sx={{
                                        color: "#7b1fa2",
                                      }}
                                    />
                                  }
                                  label="Interest Shown"
                                  size="small"
                                  // color="success"
                                  variant="outlined"
                                  onClick={
                                    user.latest_general_interest
                                      ? () => handleOpen({ ...user, ...details })
                                      : undefined
                                  }
                                  sx={{
                                    alignItems: "center",
                                    ml: 0.5,
                                    cursor: user.latest_general_interest
                                      ? "pointer"
                                      : "default",
                                    color: "#7b1fa2",
                                  }}
                                />
                              )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : candidates.length === 0 ? (
          <Typography
            sx={{
              mt: 2,
              textAlign: "center",
            }}
          >
            No candidates found.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {candidates.map((user) => {
              const details =
                candidateDetails.find((d) => d.user_id === user.user_id) || {};
              const hasPeerReview = [
                user?.peerReview,
                user?.peer_review,
                details?.peerReview,
                details?.peer_review,
              ].some((val) => val === 1 || val === true || val === "1");
              const showCareerJourney = user.has_career_journey !== 0;
              const skills = user.skills || [];
              const showAll = expandedRows[user.user_id] || false;
              const visibleSkills = showAll ? skills : skills.slice(0, 3);
              return (
                <Grid item xs={12} sm={6} md={4} key={user.id}>
                  <Card
                    elevation={3}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Main Content */}
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 2 }}
                      >
                        <Box
                          sx={{ position: "relative", display: "inline-flex" }}
                        >
                          <Avatar>
                            {details.first_name ? details.first_name[0] : "?"}
                          </Avatar>
                          {hasPeerReview && (
                            <Tooltip title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility.">
                              <VerifiedIcon
                                sx={{
                                  position: "absolute",
                                  bottom: -4,
                                  right: -4,
                                  fontSize: 18,
                                  color: "#7b1fa2",
                                  bgcolor: "background.paper",
                                  borderRadius: "50%",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1">
                            {details.masked_name}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>Country:</b> {details.country || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>City:</b> {details.city || "N/A"}
                      </Typography> */}
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>Job Search Status:</b>{" "}
                        {details.job_search_status || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>Role:</b> {user.job_role_name || user.job_role_id}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        alignItems="center"
                        useFlexGap
                        sx={{ mt: 1 }}
                      >
                        <Chip
                          label={user.job_role_name || user.job_role_id}
                          color="primary"
                          size="small"
                        />
                        {jobId && (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={skillMatchLoadingId === user.user_id}
                            onClick={() => handleSkillMatchClick(user.user_id)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              py: "1px",
                              px: 1,
                              lineHeight: 1.5,
                              minHeight: 24,
                            }}
                          >
                            {skillMatchLoadingId === user.user_id
                              ? "Loading..."
                              : "Skill Match Score"}
                          </Button>
                        )}
                      </Stack>

                      <Box sx={{ mt: 2, flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <b>Skills:</b>
                        </Typography>
                        {skills.length > 0 ? (
                          <>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: showAll
                                  ? "repeat(2, 1fr)"
                                  : "1fr",
                                gap: 0.5,
                                paddingLeft: "20px",
                                maxHeight: showAll ? "auto" : "90px",
                                overflow: "hidden",
                                transition: "max-height 0.3s ease",
                              }}
                            >
                              {visibleSkills.map((skillObj, index) => {
                                const skillName = Object.keys(skillObj)[0];
                                return (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    component="li"
                                    sx={{
                                      listStylePosition: "inside",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {skillName}
                                  </Typography>
                                );
                              })}
                            </Box>

                            {skills.length > 3 && (
                              <Button
                                onClick={() => handleToggle(user.user_id)}
                                size="small"
                                sx={{ mt: 0.5, textTransform: "none" }}
                              >
                                {showAll ? "Show Less" : "Show More"}
                              </Button>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No skills found
                          </Typography>
                        )}
                      </Box>
                    </CardContent>

                    {/* Footer Buttons */}
                    <CardActions
                      sx={{
                        mt: "auto",
                        pt: 1.5,
                        pb: 2,
                        px: 2,
                        justifyContent: "flex-start",
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {showCareerJourney && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleCareerJourneyModelOpen(user)}
                          >
                            Career Journey
                          </Button>
                        )}
                        {!user.interest_status &&
                          !user.latest_general_interest && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<PersonAddIcon fontSize="small" />}
                              onClick={() =>
                                handleOpen({ ...user, ...details })
                              }
                              sx={{
                                textTransform: "none",
                                fontWeight: 500,
                              }}
                            >
                              Interest
                            </Button>
                          )}
                        {(user.interest_status ||
                          user?.latest_general_interest) && (
                            <Chip
                              icon={
                                <CheckRoundedIcon
                                  fontSize="small"
                                  sx={{
                                    color: "#7b1fa2",
                                  }}
                                />
                              }
                              label={
                                user.latest_general_interest
                                  ? "Interest Shown"
                                  : "Already Interested"
                              }
                              size="small"
                              // color="success"
                              variant="outlined"
                              onClick={
                                user.latest_general_interest
                                  ? () => handleOpen({ ...user, ...details })
                                  : undefined
                              }
                              sx={{
                                cursor: user.latest_general_interest
                                  ? "pointer"
                                  : "default",
                                color: "#7b1fa2",
                              }}
                            />
                          )}
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Skill Matching Dialog */}
        <SkillMatchingDialog
          open={skillMatchDialogOpen}
          onClose={() => {
            setSkillMatchDialogOpen(false);
            setSelectedSkillMatchData(null);
          }}
          candidate={selectedSkillMatchData}
          job={{ job_role_name: roleNames.join(", ") }}
        />

        {selectedUser && (
          <Dialog
            open={open}
            onClose={handleClose}
            BackdropProps={{
              sx: { backdropFilter: "blur(4px)", background: "transparent" },
            }}
            sx={{
              "& .MuiDialog-paper": {
                width: "60%",
                maxWidth: "90%",
                boxShadow: "none",
                border: "1px solid #ccc",
                borderRadius: 2,
                p: 0,
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#6c5ce7",
                color: "white",
                pb: 2,
                pt: 2,
                mb: 2,
              }}
            >
              Candidate Details
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "white",
                }}
              >
                <X size={20} />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                overflowY: "auto",
                maxHeight: "80vh",
                "&::-webkit-scrollbar": { width: 8 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: 8,
                },
              }}
              dividers
            >
              {/* Candidate basic info */}
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Name:</b> {selectedUser.masked_name || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Employment Type:</b> {selectedUser.employment_type || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Willing to Relocate:</b> {selectedUser.willRelocate || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Job Function:</b> {selectedUser.job_function_name || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Job Role:</b> {selectedUser.job_role_name || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Work Setup:</b> {selectedUser.workSetupNames || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                <b>Notice Period:</b>{" "}
                {selectedUser?.notice_period_value !== undefined &&
                  selectedUser?.notice_period_value !== null
                  ? selectedUser.notice_period_value === 0
                    ? "Available Immediately"
                    : `${selectedUser.notice_period_value} ${cap(selectedUser.notice_period_unit) || ""
                    }`
                  : "N/A"}
              </Typography>

              {/* Technical Skills Table */}
              {selectedUser.skills?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Technical Skills
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Skill</b>
                          </TableCell>
                          <TableCell>
                            <b>Proficiency</b>
                          </TableCell>
                          <TableCell>
                            <b>Sub-skill</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUser.skills.map((skillObj, index) => {
                          const skillName = Object.keys(skillObj)[0];
                          const skillData = skillObj[skillName];

                          return (
                            <TableRow key={index}>
                              <TableCell>{skillName}</TableCell>
                              <TableCell>{skillData.level}</TableCell>
                              <TableCell>
                                {skillData.subSkills?.length > 0
                                  ? skillData.subSkills
                                    .map((ss) =>
                                      typeof ss === "object"
                                        ? `${ss.name} (${ss.level})`
                                        : ss,
                                    )
                                    .join(", ")
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Soft Skills Table */}
              {selectedUser.softSkills?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Soft Skills
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Soft Skill</b>
                          </TableCell>
                          <TableCell>
                            <b>Example</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUser.softSkills.map((soft) => (
                          <TableRow key={soft.id}>
                            <TableCell>{soft.soft_skill}</TableCell>
                            <TableCell>
                              {soft.evidence?.replace(/"/g, "") || "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Education Table */}
              {selectedUser.educations?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Education
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Degree</b>
                          </TableCell>

                          <TableCell>
                            <b>Field of Study</b>
                          </TableCell>
                          <TableCell>
                            <b>Location</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUser.educations.map((edu) => (
                          <TableRow key={edu.id}>
                            <TableCell>{edu.degree}</TableCell>
                            <TableCell>{edu.field_of_study}</TableCell>
                            <TableCell>{edu.location || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Certifications Table */}
              {selectedUser.certifications?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Certifications
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Certification</b>
                          </TableCell>
                          <TableCell>
                            <b>Organization</b>
                          </TableCell>
                          <TableCell>
                            <b>Issue Date</b>
                          </TableCell>
                          <TableCell>
                            <b>Expiry Date</b>
                          </TableCell>
                          <TableCell>
                            <b>Credential ID</b>
                          </TableCell>
                          <TableCell>
                            <b>Category</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUser.certifications.map((cert) => (
                          <TableRow key={cert.id}>
                            <TableCell>{cert.certification_name}</TableCell>
                            <TableCell>{cert.issuing_organization}</TableCell>
                            <TableCell>
                              {cert.issue_date
                                ? new Date(cert.issue_date).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {resolveCertificationNoExpiry(cert) === 1
                                ? "No Expiry"
                                : formatCertDate(
                                    cert.expiry_date || cert.expiryDate,
                                  ) || "N/A"}
                            </TableCell>
                            <TableCell>{cert.credential_id || "—"}</TableCell>
                            <TableCell>{cert.category || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
              {console.log(selectedUser, selectedUser.available_job_postings)}
              {/* Job Posting Selection */}
              {selectedUser?.available_job_postings?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                  >
                    Select Job Posting
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={selectedJobPosting}
                    onChange={(e) => {
                      setSelectedJobPosting(e.target.value);
                      if (error && e.target.value) {
                        setError("");
                      }
                    }}
                    placeholder="Choose a job posting"
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fafafa",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
                        },
                      },
                    }}
                  >
                    {selectedUser.available_job_postings.map((job) => (
                      <MenuItem key={job.id} value={job.id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "primary.main",
                            }}
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {job.role_name}
                          </Typography>
                          <Chip
                            label={job.experience_level}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              backgroundColor: "primary.light",
                              color: "primary.contrastText",
                            }}
                          />
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {/* Direct Interest Info Message */}
              {!jobId && !selectedJobPosting && (
                <Alert
                  icon={false}
                  sx={{
                    mb: 3,
                    backgroundColor: "#f3e5f5",
                    color: "#7b1fa2",
                    border: "1px solid #7b1fa2",
                    "& .MuiAlert-icon": {
                      color: "#7b1fa2",
                    },
                  }}
                >
                  <b>Note:</b> If you proceed without selecting a job posting,
                  you'll need to manually add interview stages when scheduling.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Message for candidate field */}
              <Alert severity="warning" sx={{ py: 0, mb: 1 }}>
                <strong>This message will be sent to the candidate along with the job request. Please review it before sending.</strong>
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Message to candidate"
                placeholder="Message for the candidate..."
                value={comment}
                onChange={handleCommentChange}
              />
              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 0.5 }}>
                  {error}
                </Typography>
              )}

              {/* Latest General Interest Information */}
              {selectedUser.latest_general_interest && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                    Latest General Interest
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Candidate Status</b>
                          </TableCell>
                          <TableCell>
                            <b>Created At</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Chip
                              label={
                                selectedUser.latest_general_interest.status
                                  ? selectedUser.latest_general_interest.status
                                    .split("_")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1),
                                    )
                                    .join(" ")
                                  : "N/A"
                              }
                              size="small"
                              color={
                                selectedUser.latest_general_interest.status ===
                                  "pending_admin"
                                  ? "warning"
                                  : selectedUser.latest_general_interest
                                    .status === "approved"
                                    ? "success"
                                    : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {selectedUser.latest_general_interest.created_at
                              ? new Date(
                                selectedUser.latest_general_interest
                                  .created_at,
                              ).toLocaleString()
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                // color="success"
                sx={{
                  backgroundColor: "#6c5ce7",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#5a4bcf",
                  },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>

      <CareerJourneyPage
        careerJourneyId={selectedUserCareerJourneyId}
        userJobRoleId={selectedUserJobRoleId}
        isOpen={modelOpen}
        isClose={handleCareerJourneyClose}
        hasPeerReview={selectedUserPeerReview}
      />
    </>
  );
};

export default RoleCandidates;
